// src/components/MarkdownRenderer.tsx
import React from 'react';

interface MarkdownRendererProps {
    content: string;
}

/**
 * 简单的 Markdown 渲染器（零依赖）
 * 支持：标题、粗体、斜体、代码块、行内代码、列表、链接、表格、分隔线
 */
export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content }) => {
    if (!content) {
        return <div style={{ padding: 24, color: '#999' }}>暂无内容</div>;
    }

    // 渲染行内样式
    const renderInline = (text: string): React.ReactNode => {
        const segments: Array<{ type: 'text' | 'bold' | 'italic' | 'code' | 'link'; content: string; url?: string }> = [];
        let current = '';
        let i = 0;

        while (i < text.length) {
            // 行内代码 `code`
            if (text[i] === '`') {
                const end = text.indexOf('`', i + 1);
                if (end !== -1) {
                    if (current) { segments.push({ type: 'text', content: current }); current = ''; }
                    segments.push({ type: 'code', content: text.substring(i + 1, end) });
                    i = end + 1;
                    continue;
                }
            }
            // 链接 [text](url)
            if (text[i] === '[') {
                const endBracket = text.indexOf(']', i + 1);
                if (endBracket !== -1 && text[endBracket + 1] === '(') {
                    const endParen = text.indexOf(')', endBracket + 2);
                    if (endParen !== -1) {
                        if (current) { segments.push({ type: 'text', content: current }); current = ''; }
                        const linkText = text.substring(i + 1, endBracket);
                        const linkUrl = text.substring(endBracket + 2, endParen);
                        segments.push({ type: 'link', content: linkText, url: linkUrl });
                        i = endParen + 1;
                        continue;
                    }
                }
            }
            // 粗体 **text**
            if (text[i] === '*' && text[i + 1] === '*') {
                const end = text.indexOf('**', i + 2);
                if (end !== -1) {
                    if (current) { segments.push({ type: 'text', content: current }); current = ''; }
                    segments.push({ type: 'bold', content: text.substring(i + 2, end) });
                    i = end + 2;
                    continue;
                }
            }
            // 斜体 *text*
            if (text[i] === '*' && text[i + 1] !== '*') {
                const end = text.indexOf('*', i + 1);
                if (end !== -1 && text[end + 1] !== '*') {
                    if (current) { segments.push({ type: 'text', content: current }); current = ''; }
                    segments.push({ type: 'italic', content: text.substring(i + 1, end) });
                    i = end + 1;
                    continue;
                }
            }
            current += text[i];
            i++;
        }
        if (current) { segments.push({ type: 'text', content: current }); }

        return segments.map((seg, idx) => {
            switch (seg.type) {
                case 'bold':
                    return <strong key={idx}>{seg.content}</strong>;
                case 'italic':
                    return <em key={idx}>{seg.content}</em>;
                case 'code':
                    return <code key={idx} style={{ background: '#f5f5f5', padding: '2px 6px', borderRadius: 4, fontSize: '0.9em', fontFamily: 'monospace' }}>{seg.content}</code>;
                case 'link':
                    return <a key={idx} href={seg.url} target="_blank" rel="noopener noreferrer" style={{ color: '#667eea', textDecoration: 'none' }}>{seg.content}</a>;
                default:
                    return <span key={idx}>{seg.content}</span>;
            }
        });
    };

    // 渲染完整 Markdown
    const renderMarkdown = (text: string) => {
        const lines = text.split('\n');
        const result: React.ReactNode[] = [];
        let i = 0;
        let inCodeBlock = false;
        let codeBlockContent: string[] = [];
        let inList = false;
        let listType: 'ul' | 'ol' = 'ul';
        let listItems: string[] = [];
        let inTable = false;
        let tableRows: string[][] = [];

        const flushCodeBlock = () => {
            if (codeBlockContent.length > 0) {
                result.push(
                    <div key={`code-${i}`} style={{
                        background: '#1e1e1e',
                        color: '#d4d4d4',
                        padding: '12px 16px',
                        borderRadius: 6,
                        overflowX: 'auto',
                        margin: '8px 0',
                        fontFamily: 'monospace',
                        fontSize: 14,
                        whiteSpace: 'pre-wrap',
                    }}>
                        {codeBlockContent.join('\n')}
                    </div>
                );
                codeBlockContent = [];
            }
        };

        const flushList = () => {
            if (listItems.length > 0) {
                const Tag = listType === 'ul' ? 'ul' : 'ol';
                result.push(
                    React.createElement(
                        Tag,
                        { key: `list-${i}`, style: { paddingLeft: 24, margin: '8px 0' } },
                        listItems.map((item, idx) => (
                            <li key={idx} style={{ margin: '4px 0' }}>{renderInline(item)}</li>
                        ))
                    )
                );
                listItems = [];
                inList = false;
            }
        };

        const flushTable = () => {
            if (tableRows.length > 0) {
                result.push(
                    <div key={`table-${i}`} style={{ overflowX: 'auto', margin: '12px 0' }}>
                        <table style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            fontSize: 14,
                            border: '1px solid #ddd',
                        }}>
                            {tableRows.map((row, rowIdx) => (
                                <tr key={rowIdx}>
                                    {row.map((cell, cellIdx) => {
                                        const isHeader = rowIdx === 0;
                                        return React.createElement(
                                            isHeader ? 'th' : 'td',
                                            {
                                                key: cellIdx,
                                                style: {
                                                    padding: '8px 12px',
                                                    border: '1px solid #ddd',
                                                    textAlign: 'left',
                                                    background: isHeader ? '#f5f5f5' : 'transparent',
                                                    fontWeight: isHeader ? 600 : 400,
                                                }
                                            },
                                            cell.trim()
                                        );
                                    })}
                                </tr>
                            ))}
                        </table>
                    </div>
                );
                tableRows = [];
                inTable = false;
            }
        };

        for (; i < lines.length; i++) {
            let line = lines[i];

            // 代码块
            if (line.startsWith('```')) {
                if (!inCodeBlock) {
                    flushCodeBlock();
                    flushList();
                    flushTable();
                    inCodeBlock = true;
                    codeBlockContent = [];
                } else {
                    flushCodeBlock();
                    inCodeBlock = false;
                }
                continue;
            }

            if (inCodeBlock) {
                codeBlockContent.push(line);
                continue;
            }

            // 表格
            // if (line.includes('|') && !line.trim().startsWith('|--')) {
            //     flushList();
            //     if (!inTable) {
            //         inTable = true;
            //         tableRows = [];
            //     }
            //     const cells = line.split('|').filter(c => c.trim() !== '');
            //     if (cells.length > 0) {
            //         tableRows.push(cells);
            //     }
            //     continue;
            // }
            // 表格 - 跳过分隔行
            if (line.includes('|')) {
                const trimmed = line.trim();
                // 如果是分隔行（如 |---|---|），跳过
                if (/^\|?[\s\-:|]+\|?$/.test(trimmed) && trimmed.includes('-')) {
                    continue;
                }
                flushList();
                if (!inTable) {
                    inTable = true;
                    tableRows = [];
                }
                const cells = line.split('|').filter(c => c.trim() !== '');
                if (cells.length > 0) {
                    tableRows.push(cells);
                }
                continue;
            }
            if (inTable && line.trim() === '') {
                flushTable();
                continue;
            }

            // 列表
            const ulMatch = line.match(/^[\s]*[-*]\s+(.+)/);
            const olMatch = line.match(/^[\s]*\d+\.\s+(.+)/);
            if (ulMatch) {
                flushTable();
                if (!inList || listType !== 'ul') {
                    flushList();
                    listType = 'ul';
                    inList = true;
                }
                listItems.push(ulMatch[1]);
                continue;
            }
            if (olMatch) {
                flushTable();
                if (!inList || listType !== 'ol') {
                    flushList();
                    listType = 'ol';
                    inList = true;
                }
                listItems.push(olMatch[1]);
                continue;
            }
            if (inList && line.trim() === '') {
                flushList();
                continue;
            }

            // 标题
            if (line.startsWith('# ')) {
                flushList();
                flushTable();
                result.push(<h1 key={i} style={{ fontSize: 32, fontWeight: 700, margin: '24px 0 16px', borderBottom: '2px solid #eee', paddingBottom: 8 }}>{renderInline(line.substring(2))}</h1>);
                continue;
            }
            if (line.startsWith('## ')) {
                flushList();
                flushTable();
                result.push(<h2 key={i} style={{ fontSize: 26, fontWeight: 600, margin: '20px 0 12px' }}>{renderInline(line.substring(3))}</h2>);
                continue;
            }
            if (line.startsWith('### ')) {
                flushList();
                flushTable();
                result.push(<h3 key={i} style={{ fontSize: 22, fontWeight: 600, margin: '16px 0 10px' }}>{renderInline(line.substring(4))}</h3>);
                continue;
            }

            // 分隔线
            if (line.trim() === '---' || line.trim() === '***' || line.trim() === '___') {
                flushList();
                flushTable();
                result.push(<hr key={i} style={{ margin: '24px 0', border: 'none', height: 1, background: '#e0e0e0' }} />);
                continue;
            }

            // 空行
            if (line.trim() === '') {
                flushList();
                continue;
            }

            // 普通段落
            flushList();
            flushTable();
            result.push(<p key={i} style={{ margin: '8px 0', lineHeight: 1.8 }}>{renderInline(line)}</p>);
        }

        flushList();
        flushTable();
        flushCodeBlock();

        return result;
    };

    return (
        <div style={{
            padding: '16px 24px',
            fontSize: 16,
            lineHeight: 1.8,
            color: '#333',
            maxWidth: '100%',
        }}>
            {renderMarkdown(content)}
        </div>
    );
};

export default MarkdownRenderer;